import { defineMessages } from 'react-intl';

import type { NotificationCategoryInput } from '__generated__/globalTypes';

export const importantNotifications = [
  'card_bought',
  'reward_delivered',
  'award_unlocked',
  'card_attached',
  'loan_proposed',
];

export const messages = defineMessages({
  announcements: {
    id: 'Notifications.announcements',
    defaultMessage: 'News',
  },
  higherBid: {
    id: 'Notifications.higherBid',
    defaultMessage: 'Outbids',
  },
  cardAttached: {
    id: 'Notifications.cardAttached',
    defaultMessage: 'New Card in Collection',
  },
  shieldRewarded: {
    id: 'Notifications.shieldRewarded',
    defaultMessage: 'Club badge unlocked',
  },
  shieldDeprived: {
    id: 'Notifications.shieldDeprived',
    defaultMessage: 'Club badge lost',
  },
  cardBought: {
    id: 'Notifications.cardBought',
    defaultMessage: 'Cards bought',
  },
  cardRevealable: {
    id: 'Notifications.cardRevealable',
    defaultMessage: 'Card revealable',
  },
  cardWithdrawals: {
    id: 'Notifications.cardWithdrawals',
    defaultMessage: 'Card withdrawals',
  },
  conversionCredit: {
    id: 'Notifications.conversionCredit',
    defaultMessage: 'Market credits',
  },
  externalDeposit: {
    id: 'Notifications.externalDeposit',
    defaultMessage: 'External deposit',
  },
  offer: {
    id: 'Notifications.offers',
    defaultMessage: 'Offers',
  },
  cardSold: {
    id: 'Notifications.cardSold',
    defaultMessage: 'Cards sold',
  },
  cardNotSold: {
    id: 'Notifications.cardsNotSold',
    defaultMessage: 'Cards not sold',
  },
  referralRewards: {
    id: 'Notifications.referralRewards',
    defaultMessage: 'Referral rewards',
  },
  so5LineupCancelled: {
    id: 'Notifications.so5LineupCancelled',
    defaultMessage: 'Lineup canceled',
  },
  kycRequests: {
    id: 'Notifications.kycRequests',
    defaultMessage: 'KYC requests',
  },
  userRelation: {
    id: 'Notifications.userRelation',
    defaultMessage: 'Social relations',
  },
  auctionReminders: {
    id: 'Notifications.auctionReminders',
    defaultMessage: 'Auction Reminders',
  },
  shopItems: {
    id: 'Notifications.shopItems',
    defaultMessage: 'Shop Items',
  },
  marketing: {
    id: 'Notifications.marketing',
    defaultMessage: 'Marketing',
  },
  missedLineup: {
    id: 'Notifications.missedLineup',
    defaultMessage: 'Missed Lineup',
  },
  autoClaim: {
    id: 'Notifications.autoClaim',
    defaultMessage: 'Auto Claim',
  },
  loan_proposed: {
    id: 'Notifications.loan',
    defaultMessage: 'Loan',
  },
  task: {
    id: 'Notifications.missions',
    defaultMessage: 'Missions',
  },
  in_game_currency: {
    id: 'Notifications.in_game_currency',
    defaultMessage: 'In Game Currency',
  },
});

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const NOTIFICATION_CATEGORIES = [
  'announcement',
  'card_attached',
  'shield_rewarded',
  'shield_deprived',
  'card_bought',
  'card_not_sold',
  'card_revealable',
  'card_sold',
  'card_withdrawal_started',
  'card_withdrawn',
  'market_credit_expiring_soon',
  'eth_deposit_pending',
  'eth_deposit_finalized',
  'higher_bid',
  'offer_accepted',
  'offer_received',
  'offer_rejected',
  'offer_cancelled',
  'offer_withdrawn',
  'offer_countered',
  'referee_reward_ready_to_claim',
  'referrer_reward_ready_to_claim',
  'so5_lineup_cancelled',
  'appearance_destroyed',
  'kyc_request_validated',
  'kyc_request_refused',
  'user_relation_followed',
  'user_relation_revealed',
  'auction_reminder_due',
  'auction_missed',
  'auction_ends_in_5_minutes',
  'auction_ending',
  'raffle_shop_item_won',
  'raffle_shop_item_lost',
  'reward_delivered',
  'user_notification',
  'missed_lineup_available',
  'auto_claimed',
  'loan_proposed',
  'task_completed',
  'task_canceled',
  'cap_almost_reached',
] as const;

type NotificationCategory = (typeof NOTIFICATION_CATEGORIES)[number];

const notificationCategoryTypes: {
  [category in NotificationCategory]: string;
} = {
  announcement: 'AnnouncementNotification',
  card_attached: 'CardCollectionNotification',
  shield_rewarded: 'CardCollectionNotification',
  shield_deprived: 'CardCollectionNotification',
  card_bought: 'AuctionNotification',
  card_not_sold: 'SaleNotification',
  card_revealable: 'CardNotification',
  card_sold: 'SaleNotification',
  card_withdrawal_started: 'CardNotification',
  card_withdrawn: 'CardNotification',
  market_credit_expiring_soon: 'ConversionCreditNotification',
  eth_deposit_pending: 'ExternalDepositNotification',
  eth_deposit_finalized: 'ExternalDepositNotification',
  higher_bid: 'AuctionNotification',
  offer_accepted: 'OfferNotification',
  offer_received: 'OfferNotification',
  offer_rejected: 'OfferNotification',
  offer_cancelled: 'OfferNotification',
  offer_countered: 'OfferNotification',
  offer_withdrawn: 'OfferNotification',
  referee_reward_ready_to_claim: 'ReferralRewardNotification',
  referrer_reward_ready_to_claim: 'ReferralRewardNotification',
  so5_lineup_cancelled: 'So5LineupNotification',
  appearance_destroyed: 'So5LineupNotification',
  kyc_request_validated: 'KycRequestNotification',
  kyc_request_refused: 'KycRequestNotification',
  user_relation_followed: 'UserRelationNotification',
  user_relation_revealed: 'UserRelationNotification',
  auction_reminder_due: 'ReminderNotification',
  auction_missed: 'ReminderNotification',
  auction_ends_in_5_minutes: 'ReminderNotification',
  auction_ending: 'ReminderNotification',
  raffle_shop_item_won: 'ShopItemNotification',
  raffle_shop_item_lost: 'ShopItemNotification',
  reward_delivered: 'DecisivePurchaseNotification',
  user_notification: 'MarketingNotification',
  missed_lineup_available: 'So5::MissedLineupNotification',
  auto_claimed: 'AutoClaimNotification',
  loan_proposed: 'LoanNotification',
  task_completed: 'Tasks::TaskNotification',
  task_canceled: 'Tasks::TaskNotification',
  cap_almost_reached: 'InGameCurrencyNotification',
};

const notificationGroups: {
  [group in keyof typeof messages]: NotificationCategory[];
} = {
  announcements: ['announcement'],
  cardBought: ['card_bought', 'reward_delivered'],
  higherBid: ['higher_bid'],
  cardSold: ['card_sold'],
  cardNotSold: ['card_not_sold'],
  conversionCredit: ['market_credit_expiring_soon'],
  externalDeposit: ['eth_deposit_pending', 'eth_deposit_finalized'],
  offer: [
    'offer_accepted',
    'offer_received',
    'offer_rejected',
    'offer_cancelled',
    'offer_withdrawn',
    'offer_countered',
  ],
  cardAttached: ['card_attached'],
  cardRevealable: ['card_revealable'],
  shieldRewarded: ['shield_rewarded'],
  shieldDeprived: ['shield_deprived'],
  cardWithdrawals: ['card_withdrawal_started', 'card_withdrawn'],
  referralRewards: [
    'referee_reward_ready_to_claim',
    'referrer_reward_ready_to_claim',
  ],
  so5LineupCancelled: ['so5_lineup_cancelled', 'appearance_destroyed'],
  kycRequests: ['kyc_request_refused', 'kyc_request_validated'],
  userRelation: ['user_relation_followed', 'user_relation_revealed'],
  auctionReminders: [
    'auction_reminder_due',
    'auction_ends_in_5_minutes',
    'auction_ending',
    'auction_missed',
  ],
  shopItems: ['raffle_shop_item_won', 'raffle_shop_item_lost'],
  marketing: ['user_notification'],
  missedLineup: ['missed_lineup_available'],
  autoClaim: ['auto_claimed'],
  loan_proposed: ['loan_proposed'],
  task: ['task_completed', 'task_canceled'],
  in_game_currency: ['cap_almost_reached'],
};

export type NotificationGroup = keyof typeof notificationGroups;

const allGroups = Object.keys(notificationGroups) as NotificationGroup[];

export const allGroupsButAnnouncements = allGroups.filter(
  c => c !== 'announcements'
);

export const getNotificationCategories = (
  groups: NotificationGroup[]
): NotificationCategoryInput[] => {
  return groups
    .flatMap(group =>
      notificationGroups[group].map(category => ({
        name: category,
        type: notificationCategoryTypes[category],
      }))
    )
    .filter(({ type }) => type !== 'ManagerProgressionNotification');
};

export const notificationCategories = getNotificationCategories(allGroups);

export const importantNotificationCategories = notificationCategories.filter(
  ({ name }) => importantNotifications.includes(name)
);
